import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Box, SxProps, Theme, Typography } from '@mui/material';
import {
  ChangeEvent,
  DragEvent,
  forwardRef,
  ForwardRefRenderFunction,
  useEffect,
  useRef,
  useState,
} from 'react';
import { mergeRefs } from 'react-merge-refs';

import { styles } from './MuiFileUploader.styles';

type ImageFormatProps = 'image/png' | 'image/jpeg' | 'image/svg' | 'image/jpg';
type VideoFormatProps = 'video/mp4';
type Format =
  | [VideoFormatProps]
  | [ImageFormatProps]
  | [ImageFormatProps, ImageFormatProps]
  | [ImageFormatProps, ImageFormatProps, ImageFormatProps]
  | [ImageFormatProps, ImageFormatProps, ImageFormatProps, ImageFormatProps];

type MuiFileUploaderProps = {
  onChange?: (file: File, src: string) => void;
  value?: File;
  format: Format;
  error?: string;
  withoutPreview?: boolean;
};

const MuiFileUploader = forwardRef<ForwardRefRenderFunction<HTMLInputElement>, MuiFileUploaderProps>(
  ({ error, format, onChange, value, withoutPreview = false }, ref) => {
    const inputRef = useRef(null);
    const [drag, setDrag] = useState(false);
    const [preview, setPreview] = useState(null);

    const formats = format.join(', ');

    let file: File;

    const dragStartHandler = (e: DragEvent) => {
      e.preventDefault();
      setDrag(true);
    };

    const dragLeaveHandler = (e: DragEvent) => {
      e.preventDefault();
      setDrag(false);
    };

    const onDropHandler = (e: DragEvent) => {
      e.preventDefault();
      const reader = new FileReader();
      file = e.dataTransfer.files[0];

      if (file) {
        reader.readAsDataURL(file);
      }

      let src: string;

      reader.onloadend = () => {
        src = URL.createObjectURL(file);

        if (!withoutPreview) setPreview(src);
        if (onChange) onChange(file, src);
        setDrag(false);
      };
    };

    const inputChangeHandler = async (e: ChangeEvent) => {
      e.preventDefault();
      const reader = new FileReader();
      file = inputRef.current.files[0];

      if (file) {
        reader.readAsDataURL(file);
      }

      let src: string;

      reader.onloadend = () => {
        src = URL.createObjectURL(file);

        if (!withoutPreview) setPreview(src);
        if (onChange) onChange(file, src);
      };
    };

    useEffect(() => {
      if (!value && preview) {
        setPreview(null);
      }
    }, [value]);

    return (
      <Box
        sx={[
          styles.root,
          { height: preview ? 220 : 80 },
          drag && styles.dragOver,
          !!error && styles.error,
        ] as SxProps<Theme>}
      >
        {preview && formats.includes('video') && (
          <Box component="video" controls src={preview} sx={styles.media}>
            <track kind="captions" label="english_captions" src="" srcLang="en" />
            Your browser does not support HTML5 video.
          </Box>
        )}

        {preview && formats.includes('image') && (
          <Box
            alt={preview}
            component="img"
            height="100%"
            src={preview}
            sx={styles.media}
            width="100%"
          />
        )}

        {!preview && drag && (
          <Box
            onDragLeave={dragLeaveHandler}
            onDragOver={dragStartHandler}
            onDragStart={dragStartHandler}
            onDrop={onDropHandler}
            sx={styles.dropArea}
          >
            <CloudUploadOutlinedIcon color="primary" sx={styles.icon} />
            <Typography color="text.secondary" variant="body2">
              Drop file here
            </Typography>
          </Box>
        )}

        {!preview && !drag && (
          <Box
            component="label"
            onDragLeave={dragLeaveHandler}
            onDragOver={dragStartHandler}
            onDragStart={dragStartHandler}
            sx={styles.dropArea}
          >
            <input
              ref={mergeRefs([ref, inputRef])}
              accept={formats}
              onChange={inputChangeHandler}
              style={{ display: 'none' }}
              type="file"
            />
            {error ? (
              <ErrorOutlineIcon color="error" sx={styles.icon} />
            ) : (
              <CloudUploadOutlinedIcon color="action" sx={styles.icon} />
            )}
            <Typography
              color={error ? 'error' : 'text.secondary'}
              textAlign="center"
              variant="body2"
            >
              {error ?? 'Drag & Drop or click to upload'}
            </Typography>
          </Box>
        )}
      </Box>
    );
  },
);

export { MuiFileUploader };
